import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Search, X } from "lucide-react";
import axios from "axios";
import { useLang } from "../context/LanguageContext";
import usePhotos from "../hooks/usePhotos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAGLIERE_2 =
  "https://customer-assets-0z36b82j.emergentagent.net/job_casale-tradizione/artifacts/0cc27ceec016fcb7_80792c3a-d887-4103-8057-88c350395a56.jpeg";

const TABS = [
  { key: "degustazione", label: { it: "Menù Degustazione", en: "Tasting Menu" }, testid: "menu-tab-degustazione" },
  { key: "carta", label: { it: "Alla Carta", en: "À La Carte" }, testid: "menu-tab-carta" },
  { key: "taglieri", label: { it: "I Taglieri", en: "Boards" }, testid: "menu-tab-taglieri" },
  { key: "dolci", label: { it: "Dolci & Vini", en: "Desserts & Wine" }, testid: "menu-tab-dolci" },
];

function DishRow({ dish, lang }) {
  return (
    <div data-testid={`menu-item-${dish.name.it.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="group">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="font-serif-display text-xl sm:text-2xl text-[#FAF7F2] group-hover:text-[#C9A265] transition-colors duration-300">
          {dish.name[lang] || dish.name.it}
        </h3>
        <span className="flex-1 border-b border-dotted border-[rgba(201,162,101,0.3)] translate-y-[-6px]" />
        <span className="font-serif-display text-xl text-[#C9A265] whitespace-nowrap">€{dish.price}</span>
      </div>
      {(dish.desc?.[lang] || dish.desc?.it) && <p className="mt-2 text-sm text-[#7A7267] leading-relaxed">{dish.desc[lang] || dish.desc.it}</p>}
    </div>
  );
}

export default function MenuSection() {
  const [tab, setTab] = useState("degustazione");
  const { lang, setLang } = useLang();
  const photos = usePhotos();
  const [menu, setMenu] = useState(null);

  useEffect(() => {
    axios.get(`${API}/menu`).then((r) => setMenu(r.data)).catch(() => setMenu(null));
  }, []);

  const [wineQuery, setWineQuery] = useState("");
  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const q = norm(wineQuery.trim());
  const matchWine = (dish, group) =>
    !q ||
    [dish.name?.it, dish.name?.en, dish.desc?.it, dish.desc?.en, dish.region?.it, dish.region?.en, group?.group?.it, group?.group?.en]
      .some((s) => norm(s).includes(q));
  const filteredVini = (menu?.vini || [])
    .map((g) => ({ ...g, items: g.items.filter((d) => matchWine(d, g)) }))
    .filter((g) => g.items.length > 0);
  const totalWines = filteredVini.reduce((n, g) => n + g.items.length, 0);

  return (
    <section id="menu" data-testid="menu-section" className="py-24 lg:py-32 bg-[#171512]">
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <span className="eyebrow">{lang === "it" ? "Il Menù" : "The Menu"}</span>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#FAF7F2]">
            {lang === "it" ? (
              <>Due anime, <span className="italic text-[#C9A265]">una tavola</span></>
            ) : (
              <>Two souls, <span className="italic text-[#C9A265]">one table</span></>
            )}
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-base text-[#B0A89C]">
            {lang === "it"
              ? "Da una parte la memoria del territorio, dall'altra la curiosità del presente. Al centro, sempre, la materia prima."
              : "On one side the memory of our land, on the other the curiosity of the present. At the centre, always, the finest ingredients."}
          </p>
          <div className="mt-6 inline-flex rounded-full border border-[rgba(201,162,101,0.3)] overflow-hidden">
            {["it", "en"].map((l) => (
              <button
                key={l}
                data-testid={`menu-lang-toggle-${l}`}
                onClick={() => setLang(l)}
                className={`px-5 py-2 text-[11px] uppercase tracking-[0.25em] font-cinzel transition-colors duration-300 ${
                  lang === l ? "bg-[#C9A265] text-[#0D0C0A]" : "text-[#B0A89C] hover:text-[#C9A265]"
                }`}
              >
                {l === "it" ? "Italiano" : "English"}
              </button>
            ))}
          </div>
          <div className="mt-5">
            <a
              href={`${API}/menu.pdf?lang=${lang}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="menu-pdf-download"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-cinzel text-[#B0A89C] hover:text-[#C9A265] transition-colors duration-300 border-b border-transparent hover:border-[#C9A265] pb-0.5"
            >
              <Download size={13} /> {lang === "it" ? "Scarica il menù in PDF" : "Download the menu as PDF"}
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-12 flex flex-wrap justify-center gap-3"
        >
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setTab(t.key)}
              className={`px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-cinzel rounded-full border transition-all duration-300 ${
                tab === t.key
                  ? "bg-[#C9A265] text-[#0D0C0A] border-[#C9A265]"
                  : "border-[rgba(201,162,101,0.3)] text-[#B0A89C] hover:border-[#C9A265] hover:text-[#C9A265]"
              }`}
            >
              {t.label[lang]}
            </button>
          ))}
        </motion.div>

        {!menu ? (
          <p data-testid="menu-loading" className="mt-14 text-center text-[#7A7267] font-cinzel tracking-[0.25em] text-xs uppercase">
            {lang === "it" ? "Caricamento del menù..." : "Loading the menu..."}
          </p>
        ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={tab + lang}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`menu-list-${tab}`}
            className="mt-14"
          >
            {tab === "degustazione" && (
              <div className="max-w-2xl mx-auto bg-[#0D0C0A] border border-[rgba(201,162,101,0.25)] rounded-2xl p-8 lg:p-12 text-center">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A265] font-cinzel">
                  {lang === "it" ? "Menù Degustazione della Tradizione" : "Traditional Tasting Menu"}
                </p>
                <div className="mt-8 space-y-5">
                  {menu.degustazione.courses.map((course, i) => (
                    <div key={i}>
                      <p className="font-serif-display text-lg sm:text-xl text-[#FAF7F2] leading-snug">
                        {course.name[lang] || course.name.it}
                      </p>
                      {(course.desc?.[lang] || course.desc?.it) && (
                        <p className="mt-1.5 text-sm text-[#7A7267] leading-relaxed">{course.desc[lang] || course.desc.it}</p>
                      )}
                      {i < menu.degustazione.courses.length - 1 && (
                        <p className="mt-5 text-[#C9A265] text-[10px] tracking-[0.5em]">✦ ✦ ✦</p>
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-10 font-serif-display text-4xl text-[#C9A265]">€{menu.degustazione.price}</p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.25em] text-[#7A7267] font-cinzel">
                  {lang === "it" ? "Ordinabile per l'intero tavolo" : "Available for the whole table only"}
                </p>
              </div>
            )}

            {tab === "carta" && (
              <div className="space-y-14">
                {menu.carta.map((course) => (
                  <div key={course.group.it}>
                    <p className="text-center text-[11px] uppercase tracking-[0.35em] text-[#C9A265] font-cinzel mb-8">
                      {course.group[lang]}
                    </p>
                    <div className="grid md:grid-cols-2 gap-x-14 gap-y-10">
                      {course.items.map((dish) => (
                        <DishRow key={dish.name.it} dish={dish} lang={lang} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "taglieri" && (
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {menu.taglieri.map((board, bi) => (
                  <div
                    key={board.name.it}
                    data-testid={`tagliere-${board.name.it.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="bg-[#0D0C0A] border border-[rgba(201,162,101,0.25)] rounded-2xl p-8 hover:border-[rgba(201,162,101,0.5)] transition-colors duration-500"
                  >
                    <h3 className="font-serif-display text-2xl text-[#FAF7F2]">{board.name[lang] || board.name.it}</h3>
                    <ul className="mt-6 space-y-4">
                      {board.items.map((item, i) => (
                        <li key={i} className="text-sm text-[#B0A89C] leading-relaxed flex gap-3">
                          <span className="text-[#C9A265] shrink-0">✦</span>
                          <span>
                            {item.name[lang] || item.name.it}
                            {(item.desc?.[lang] || item.desc?.it) && (
                              <span className="block mt-0.5 text-xs text-[#7A7267]">{item.desc[lang] || item.desc.it}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {(photos[`menu-taglieri-${bi + 1}`] || (bi === 1 && TAGLIERE_2)) && (
                      <img
                        data-testid={`menu-tagliere-photo-${bi + 1}`}
                        src={photos[`menu-taglieri-${bi + 1}`] || TAGLIERE_2}
                        alt={board.name[lang] || board.name.it}
                        className="mt-6 rounded-2xl h-44 w-full object-cover border border-[rgba(201,162,101,0.2)]"
                      />
                    )}
                    <p className="mt-8 font-serif-display text-2xl text-[#C9A265]">€{board.price[lang] || board.price.it}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === "dolci" && (
              <div className="space-y-14">
                <div className="max-w-md mx-auto">
                  <div className="relative">
                    <Search size={15} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#7A7267] pointer-events-none" />
                    <input
                      data-testid="vini-search-input"
                      type="text"
                      value={wineQuery}
                      onChange={(e) => setWineQuery(e.target.value)}
                      placeholder={lang === "it" ? "Cerca un vino… (es. Barolo, Francia, bollicine)" : "Search a wine… (e.g. Barolo, France, sparkling)"}
                      className="w-full bg-[#0D0C0A] border border-[rgba(201,162,101,0.25)] rounded-full pl-12 pr-12 py-3.5 text-sm text-[#FAF7F2] placeholder:text-[#7A7267] focus:outline-none focus:border-[#C9A265] transition-colors duration-300"
                    />
                    {wineQuery && (
                      <button
                        type="button"
                        data-testid="vini-search-clear"
                        onClick={() => setWineQuery("")}
                        aria-label={lang === "it" ? "Cancella ricerca" : "Clear search"}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7A7267] hover:text-[#C9A265] transition-colors duration-300"
                      >
                        <X size={15} />
                      </button>
                    )}
                  </div>
                  {q && (
                    <p data-testid="vini-search-count" className="mt-3 text-center text-[11px] uppercase tracking-[0.25em] font-cinzel text-[#7A7267]">
                      {lang === "it"
                        ? `${totalWines} ${totalWines === 1 ? "vino trovato" : "vini trovati"}`
                        : `${totalWines} ${totalWines === 1 ? "wine" : "wines"} found`}
                    </p>
                  )}
                </div>
                {!q && (
                <div>
                  <p className="text-center text-[11px] uppercase tracking-[0.35em] text-[#C9A265] font-cinzel mb-8">
                    {lang === "it" ? "Dolci" : "Desserts"}
                  </p>
                  <div className="grid md:grid-cols-2 gap-x-14 gap-y-10">
                    {menu.dolci.map((dish) => (
                      <DishRow key={dish.name.it} dish={dish} lang={lang} />
                    ))}
                  </div>
                </div>
                )}
                {q && totalWines === 0 && (
                  <p data-testid="vini-search-empty" className="text-center font-serif-display italic text-xl text-[#7A7267]">
                    {lang === "it"
                      ? `Nessun vino trovato per "${wineQuery.trim()}"`
                      : `No wines found for "${wineQuery.trim()}"`}
                  </p>
                )}
                {filteredVini.map((group, gi) => {
                  const clusters = [];
                  group.items.forEach((dish) => {
                    const key = dish.region?.it || null;
                    const last = clusters[clusters.length - 1];
                    if (last && last.key === key) last.items.push(dish);
                    else clusters.push({ key, label: dish.region || null, items: [dish] });
                  });
                  return (
                    <div key={gi} data-testid={`vini-group-${gi}`}>
                      <p className="text-center text-[11px] uppercase tracking-[0.35em] text-[#C9A265] font-cinzel mb-8">
                        {group.group[lang] || group.group.it}
                      </p>
                      <div className="space-y-10">
                        {clusters.map((cl, ci) => (
                          <div key={cl.key ?? `plain-${ci}`}>
                            {cl.label && (
                              <p
                                data-testid={`vini-region-${gi}-${ci}`}
                                className="text-center font-serif-display italic text-xl text-[#B0A89C] mb-6"
                              >
                                {cl.label[lang] || cl.label.it}
                              </p>
                            )}
                            <div className="grid md:grid-cols-2 gap-x-14 gap-y-10">
                              {cl.items.map((dish) => (
                                <DishRow key={dish.name.it} dish={dish} lang={lang} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        )}

      </div>
    </section>
  );
}
